import { useState, useEffect } from "react";

// ─── PROGRAM DATA ────────────────────────────────────────────────────────────
const RACE_DATE = new Date("2026-10-23");
const PROGRAM_START = new Date("2026-03-30"); // ~27 weeks before race

const PHASES = [
  { id: 1, name: "Aerobic Base Rebuild", weeks: [1, 12], color: "#4ade80", desc: "Zone 2 discipline only. Build aerobic infrastructure." },
  { id: 2, name: "Threshold Development", weeks: [13, 20], color: "#facc15", desc: "Raise threshold toward race pace. Interval work begins." },
  { id: 3, name: "Race-Specific Sharpening", weeks: [21, 27], color: "#f97316", desc: "Race-pace specificity. Full simulations. Fatigue resistance." },
];

const GATES = [
  { week: 6, metric: "HR at 11:00/mi pace", target: "< 148 bpm", field: "hr_at_11min", targetVal: 148, direction: "below", critical: false },
  { week: 12, metric: "Continuous run at HR 143", target: "≤ 11:00/mi, no walk breaks", field: "z2_pace_wk12", targetVal: 660, direction: "below", critical: true },
  { week: 20, metric: "Threshold pace", target: "≤ 9:00/mi", field: "threshold_pace", targetVal: 540, direction: "below", critical: true },
];

const DAILY_SCHEDULE = {
  Monday: {
    am: { label: "Zone 2 Run/Walk", duration: "40–45 min", detail: "HR cap 145 bpm. Run/walk protocol. No pace target — HR discipline only.", type: "aerobic" },
    pm: { label: "Sled Accessory", duration: "30 min", detail: "Bulgarian split squat 4×6/leg · Hip thrust 4×8 · Leg press 3×10", type: "strength" }
  },
  Tuesday: {
    am: { label: "Full Body Neural Strength", duration: "45 min", detail: "Trap bar deadlift 5×3 · Bench press 5×3 · Weighted pull-up 4×3 · Farmer carry 3×40m", type: "strength" },
    pm: { label: "Zone 2 Run/Walk", duration: "30 min", detail: "Same HR discipline as Monday AM. Second aerobic stimulus. NOT a threshold session.", type: "aerobic" }
  },
  Wednesday: {
    am: { label: "Strength + Sled Priority", duration: "45 min", detail: "Front squat 5×3 · RDL 4×5 · Sled push 5×20m · Walking lunge 3×16 steps", type: "strength" },
    pm: { label: "Aerobic Finisher", duration: "20 min", detail: "Row or bike only — NOT running. HR cap 145. Zone 2 stimulus without leg load.", type: "aerobic" }
  },
  Thursday: {
    am: { label: "Full Body Neural Strength", duration: "45 min", detail: "Back squat 5×3 · Push press 4×4 · Chest-supported row 4×6 · Standing press 4×4", type: "strength" },
    pm: { label: "Hill Sprints", duration: "25 min", detail: "10 × 8–10 sec max effort. 60–90 sec walk recovery. ONE high-intensity run session per week.", type: "power" }
  },
  Friday: {
    am: { label: "Strength + Station Focus", duration: "45 min", detail: "RDL 4×5 · Pause squat 3×4 · Sled push 4×20m · Row intervals 6×250m · Sandbag lunge 3×20m", type: "strength" },
    pm: { label: "Zone 2", duration: "20–25 min", detail: "Bike or easy row. HR cap 143. Recovery-adjacent. Not a finisher.", type: "aerobic" }
  },
  Saturday: {
    am: { label: "PRIMARY HYROX SESSION", duration: "90 min", detail: "Wks 1–6: 4 rounds · 1km run + sled push/pull + 500m row + 15 BBJ. Wks 7–12: 5 rounds full station sequence.", type: "hyrox" },
    pm: null
  },
  Sunday: {
    am: { label: "Long Aerobic Run", duration: "75–90 min", detail: "Run/walk. HR cap 143. Primary driver of mitochondrial density over 12 weeks.", type: "aerobic" },
    pm: null
  }
};

// Phase 2 modifications
const PHASE2_OVERRIDES = {
  Tuesday: { pm: { label: "THRESHOLD INTERVALS", duration: "30–35 min", detail: "Wks 13–16: 4×6 min at RPE 7–8, HR 165–172, 90s walk recovery. Wks 17–20: 5×8 min same HR.", type: "threshold" } },
  Wednesday: { pm: { label: "HYROX Finisher", duration: "20 min", detail: "10 rounds: 200m run / 20 wall balls. Alt weeks: ski erg 500m for run.", type: "hyrox" } },
  Friday: { pm: { label: "HYROX Finisher", duration: "20 min", detail: "5 rounds: 400m run / 25 wall balls / 10 burpee broad jumps. Aggressive. Race-pace effort.", type: "hyrox" } },
};

const PHASE3_OVERRIDES = {
  Monday: { pm: { label: "Mobility & Activation", duration: "20 min", detail: "Hip activation, ankle mobility, glute activation. Legs must arrive Tuesday fresh.", type: "recovery" } },
  Tuesday: { pm: { label: "RACE-PACE INTERVALS", duration: "30–35 min", detail: "6×1km at exact 8:35/mi (4:19/km). 90s recovery. Primary race-specificity stimulus.", type: "race-pace" } },
  Wednesday: { pm: { label: "Running Intervals", duration: "20 min", detail: "200m at race pace / 60s walk / repeat. Pure running — no circuits.", type: "race-pace" } },
  Friday: { pm: { label: "Running Intervals", duration: "20 min", detail: "400m at race pace / 90s recovery / repeat for 20 min.", type: "race-pace" } },
};

const TAPER = [
  { date: "Oct 17", day: "Sat", session: "3 rounds: 1km run + sled push + sled pull + 15 wall balls. Controlled, sharp, not hard." },
  { date: "Oct 18", day: "Sun", session: "45–60 min easy aerobic. HR cap 140." },
  { date: "Oct 19", day: "Mon", session: "30–40 min easy run + 4 relaxed strides at race pace." },
  { date: "Oct 20", day: "Tue", session: "3×1km race pace. Light neural lift: Trap bar 2×3, Bench 2×3. Do not fatigue." },
  { date: "Oct 21", day: "Wed", session: "30–35 min easy aerobic only." },
  { date: "Oct 22", day: "Thu", session: "20–25 min jog + 3×20 sec strides + light wall balls. That's it." },
  { date: "Oct 23", day: "RACE", session: "Warm-up: 20–25 min jog + dynamic mobility + 3 short strides. Race." },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function getWeekNumber(date) {
  const diff = date - PROGRAM_START;
  if (diff < 0) return 0;
  return Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
}

function getCurrentPhase(week) {
  if (week >= 1 && week <= 12) return 1;
  if (week >= 13 && week <= 20) return 2;
  if (week >= 21 && week <= 27) return 3;
  return 0;
}

function getDayOfWeek(date) {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][date.getDay()];
}

function getTodaySchedule(week) {
  const day = getDayOfWeek(new Date());
  const base = DAILY_SCHEDULE[day];
  if (!base) return null;
  const phase = getCurrentPhase(week);
  if (phase === 2 && PHASE2_OVERRIDES[day]) {
    return { ...base, ...PHASE2_OVERRIDES[day] };
  }
  if (phase === 3 && PHASE3_OVERRIDES[day]) {
    return { ...base, ...PHASE3_OVERRIDES[day] };
  }
  return base;
}

function paceToSeconds(paceStr) {
  if (!paceStr) return null;
  const parts = paceStr.split(":");
  return parseInt(parts[0]) * 60 + parseInt(parts[1]);
}

function secondsToPace(s) {
  if (!s) return "--:--";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function daysUntilRace() {
  const now = new Date();
  const diff = RACE_DATE - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const TYPE_COLORS = {
  aerobic: "#4ade80",
  strength: "#60a5fa",
  power: "#f472b6",
  hyrox: "#f97316",
  threshold: "#facc15",
  "race-pace": "#fb923c",
  recovery: "#94a3b8",
};

// ─── STORAGE HELPERS ─────────────────────────────────────────────────────────
async function saveLog(entry) {
  const key = `log:${entry.date}`;
  try { await window.storage.set(key, JSON.stringify(entry)); } catch(e) { console.error(e); }
}

async function loadLog(date) {
  const key = `log:${date}`;
  try {
    const r = await window.storage.get(key);
    return r ? JSON.parse(r.value) : null;
  } catch(e) { return null; }
}

async function loadAllLogs() {
  try {
    const keys = await window.storage.list("log:");
    const logs = [];
    for (const k of keys.keys) {
      try {
        const r = await window.storage.get(k);
        if (r) logs.push(JSON.parse(r.value));
      } catch(e) {}
    }
    return logs.sort((a,b) => a.date.localeCompare(b.date));
  } catch(e) { return []; }
}

async function saveGateData(gateData) {
  try { await window.storage.set("gates", JSON.stringify(gateData)); } catch(e) {}
}

async function loadGateData() {
  try {
    const r = await window.storage.get("gates");
    return r ? JSON.parse(r.value) : {};
  } catch(e) { return {}; }
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function StatCard({ label, value, unit, sub, color = "#4ade80", small = false }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 12,
      padding: small ? "14px 16px" : "20px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: small ? 24 : 32, fontWeight: 700, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontSize: 13, color: "#475569" }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SessionBlock({ session, label }) {
  if (!session) return null;
  const color = TYPE_COLORS[session.type] || "#64748b";
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: `1px solid ${color}33`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 10,
      padding: "14px 16px",
      marginBottom: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <span style={{ fontSize: 10, color, textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace" }}>{label}</span>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0", marginTop: 2 }}>{session.label}</div>
        </div>
        <span style={{ fontSize: 12, color: "#475569", background: "rgba(255,255,255,0.05)", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>{session.duration}</span>
      </div>
      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{session.detail}</div>
    </div>
  );
}

function GateCard({ gate, value, passed }) {
  const status = value === null ? "pending" : passed ? "passed" : "missed";
  const statusColor = { pending: "#64748b", passed: "#4ade80", missed: "#f87171" }[status];
  const statusLabel = { pending: "PENDING", passed: "PASSED ✓", missed: "NOT MET" }[status];
  return (
    <div style={{
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${statusColor}44`,
      borderRadius: 10,
      padding: "14px 16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}>
      <div>
        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>Week {gate.week} Gate</div>
        <div style={{ fontSize: 14, color: "#cbd5e1", fontWeight: 500, marginTop: 3 }}>{gate.metric}</div>
        <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Target: {gate.target}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 11, color: statusColor, fontWeight: 700, letterSpacing: "0.08em", fontFamily: "monospace" }}>{statusLabel}</div>
        {value !== null && <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>{value}</div>}
      </div>
    </div>
  );
}

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = ["Today", "Log", "Progress", "Plan", "Gates"];

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
export default function HyroxDashboard() {
  const [tab, setTab] = useState("Today");
  const [today] = useState(new Date());
  const [currentWeek] = useState(Math.max(1, getWeekNumber(today)));
  const [currentPhase] = useState(getCurrentPhase(currentWeek));
  const [todaySchedule] = useState(getTodaySchedule(currentWeek));
  const [logs, setLogs] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  const [gateData, setGateData] = useState({});
  const [logForm, setLogForm] = useState({
    date: today.toISOString().split("T")[0],
    zone2_time: "",
    zone2_hr_avg: "",
    run_pace: "",
    run_miles: "",
    rpe: "",
    sleep_hrs: "",
    hrv: "",
    calories: "",
    protein: "",
    water: "",
    sled_load: "",
    row_500_split: "",
    threshold_pace: "",
    notes: "",
    completed: false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gateForm, setGateForm] = useState({});

  const dateStr = today.toISOString().split("T")[0];
  const daysLeft = daysUntilRace();
  const phaseInfo = PHASES.find(p => p.id === currentPhase);
  const dayName = getDayOfWeek(today);
  const isTaper = currentWeek > 27;

  useEffect(() => {
    loadAllLogs().then(setLogs);
    loadLog(dateStr).then(setTodayLog);
    loadGateData().then(d => { setGateData(d); setGateForm(d); });
  }, []);

  // ── compliance score ──
  function computeWeeklyScore() {
    const weekLogs = logs.filter(l => {
      const lDate = new Date(l.date);
      const lWeek = getWeekNumber(lDate);
      return lWeek === currentWeek;
    });
    if (weekLogs.length === 0) return null;
    let score = 0;
    let maxScore = 0;
    weekLogs.forEach(l => {
      maxScore += 100;
      if (l.completed) score += 40;
      if (l.zone2_time && parseInt(l.zone2_time) >= 30) score += 20;
      if (l.sleep_hrs && parseFloat(l.sleep_hrs) >= 7) score += 20;
      if (l.calories && parseInt(l.calories) > 0) score += 10;
      if (l.protein && parseInt(l.protein) >= 180) score += 10;
    });
    return Math.round((score / maxScore) * 100);
  }

  const weekScore = computeWeeklyScore();

  // ── z2 mins this week ──
  const weekZ2 = logs
    .filter(l => getWeekNumber(new Date(l.date)) === currentWeek)
    .reduce((sum, l) => sum + (parseInt(l.zone2_time) || 0), 0);

  const weekMiles = logs
    .filter(l => getWeekNumber(new Date(l.date)) === currentWeek)
    .reduce((sum, l) => sum + (parseFloat(l.run_miles) || 0), 0);

  async function handleSaveLog() {
    setSaving(true);
    const entry = { ...logForm, date: dateStr };
    await saveLog(entry);
    setTodayLog(entry);
    const updated = logs.filter(l => l.date !== dateStr);
    setLogs([...updated, entry].sort((a,b) => a.date.localeCompare(b.date)));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function handleSaveGates() {
    await saveGateData(gateForm);
    setGateData(gateForm);
  }

  const scoreColor = weekScore === null ? "#64748b" : weekScore >= 80 ? "#4ade80" : weekScore >= 60 ? "#facc15" : "#f87171";

  // ─── PHASE BAR ───────────────────────────────────────────────────────────
  function PhaseBar() {
    const totalWeeks = 27;
    const pct = Math.min(100, ((currentWeek - 1) / totalWeeks) * 100);
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          {PHASES.map(p => (
            <div key={p.id} style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: 10, color: p.id === currentPhase ? p.color : "#475569", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "monospace", fontWeight: p.id === currentPhase ? 700 : 400 }}>
                Phase {p.id}
              </div>
              <div style={{ fontSize: 10, color: "#334155" }}>Wk {p.weeks[0]}–{p.weeks[1]}</div>
            </div>
          ))}
          <div style={{ textAlign: "center", flex: 0.5 }}>
            <div style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "monospace" }}>Taper</div>
            <div style={{ fontSize: 10, color: "#334155" }}>Wk 28</div>
          </div>
        </div>
        <div style={{ height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden", position: "relative" }}>
          {PHASES.map(p => {
            const left = ((p.weeks[0] - 1) / 27) * 100;
            const width = ((p.weeks[1] - p.weeks[0] + 1) / 27) * 100;
            return (
              <div key={p.id} style={{
                position: "absolute", left: `${left}%`, width: `${width}%`,
                height: "100%", background: p.id === currentPhase ? `${p.color}55` : `${p.color}22`,
                borderRight: "1px solid #0f172a"
              }} />
            );
          })}
          <div style={{ position: "absolute", left: 0, width: `${pct}%`, height: "100%", background: phaseInfo ? `${phaseInfo.color}cc` : "#4ade80", borderRadius: 4, transition: "width 0.3s" }} />
          <div style={{ position: "absolute", left: `${pct}%`, top: -3, width: 14, height: 14, background: phaseInfo?.color || "#4ade80", borderRadius: "50%", transform: "translateX(-50%)", border: "2px solid #0f172a" }} />
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: "#475569", textAlign: "center" }}>
          Week {currentWeek} of 27 · {daysLeft} days to race
        </div>
      </div>
    );
  }

  // ─── TODAY TAB ────────────────────────────────────────────────────────────
  function TodayTab() {
    return (
      <div>
        <PhaseBar />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
          <StatCard label="Days to Race" value={daysLeft} color="#f97316" />
          <StatCard label="Current Week" value={currentWeek} unit="/ 27" color={phaseInfo?.color || "#4ade80"} />
          <StatCard label="Week Score" value={weekScore !== null ? `${weekScore}%` : "—"} color={scoreColor} sub="compliance" />
          <StatCard label="Z2 This Week" value={weekZ2} unit="min" color="#4ade80" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: phaseInfo?.color || "#4ade80", boxShadow: `0 0 8px ${phaseInfo?.color}` }} />
            <span style={{ fontSize: 13, color: phaseInfo?.color, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>
              {isTaper ? "TAPER WEEK" : phaseInfo ? `Phase ${currentPhase}: ${phaseInfo.name}` : "Pre-Program"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16, padding: "10px 14px", background: "rgba(255,255,255,0.02)", borderRadius: 8, borderLeft: `2px solid ${phaseInfo?.color}55` }}>
            {phaseInfo?.desc}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace", marginBottom: 12 }}>
            {dayName}'s Sessions
          </div>
          {isTaper ? (
            <div style={{ background: "rgba(249,115,22,0.08)", border: "1px solid #f9731633", borderRadius: 10, padding: 16 }}>
              <div style={{ color: "#f97316", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>TAPER WEEK</div>
              {TAPER.map((t, i) => (
                <div key={i} style={{ fontSize: 12, color: t.day === "RACE" ? "#f97316" : "#94a3b8", padding: "6px 0", borderBottom: i < TAPER.length - 1 ? "1px solid #1e293b" : "none" }}>
                  <span style={{ color: "#64748b", minWidth: 60, display: "inline-block" }}>{t.date} {t.day}</span> {t.session}
                </div>
              ))}
            </div>
          ) : todaySchedule ? (
            <>
              {todaySchedule.am && <SessionBlock session={todaySchedule.am} label="AM" />}
              {todaySchedule.pm && <SessionBlock session={todaySchedule.pm} label="PM" />}
            </>
          ) : (
            <div style={{ color: "#475569", fontSize: 13 }}>No schedule data for today.</div>
          )}
        </div>

        {todayLog && (
          <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(74,222,128,0.06)", border: "1px solid #4ade8033", borderRadius: 10, fontSize: 12, color: "#64748b" }}>
            ✓ Logged today · RPE {todayLog.rpe || "—"} · Z2 {todayLog.zone2_time || "—"} min · Sleep {todayLog.sleep_hrs || "—"} hrs
          </div>
        )}
      </div>
    );
  }

  // ─── LOG TAB ─────────────────────────────────────────────────────────────
  function LogTab() {
    const fields = [
      { section: "Training", items: [
        { key: "zone2_time", label: "Zone 2 Time", unit: "min", type: "number" },
        { key: "zone2_hr_avg", label: "Avg HR (Z2)", unit: "bpm", type: "number" },
        { key: "run_pace", label: "Run Pace", unit: "min/mi (e.g. 10:30)", type: "text" },
        { key: "run_miles", label: "Miles Run", unit: "mi", type: "number" },
        { key: "sled_load", label: "Sled Load", unit: "kg", type: "number" },
        { key: "row_500_split", label: "Row 500m Split", unit: "e.g. 1:55", type: "text" },
        { key: "threshold_pace", label: "Threshold Pace", unit: "min/mi", type: "text" },
        { key: "rpe", label: "Session RPE", unit: "/10", type: "number" },
      ]},
      { section: "Recovery", items: [
        { key: "sleep_hrs", label: "Sleep", unit: "hrs", type: "number" },
        { key: "hrv", label: "HRV", unit: "ms", type: "number" },
      ]},
      { section: "Nutrition", items: [
        { key: "calories", label: "Calories", unit: "kcal", type: "number" },
        { key: "protein", label: "Protein", unit: "g", type: "number" },
        { key: "water", label: "Water", unit: "oz", type: "number" },
      ]},
    ];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>Daily Log</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{dateStr} · Week {currentWeek}</div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <div style={{
              width: 40, height: 22, borderRadius: 11, background: logForm.completed ? "#4ade80" : "#1e293b",
              border: `1px solid ${logForm.completed ? "#4ade80" : "#334155"}`,
              position: "relative", transition: "all 0.2s", cursor: "pointer"
            }} onClick={() => setLogForm(f => ({ ...f, completed: !f.completed }))}>
              <div style={{ position: "absolute", top: 2, left: logForm.completed ? 20 : 2, width: 16, height: 16, borderRadius: "50%", background: logForm.completed ? "#0f172a" : "#475569", transition: "left 0.2s" }} />
            </div>
            <span style={{ fontSize: 12, color: "#64748b" }}>Session Complete</span>
          </label>
        </div>

        {fields.map(section => (
          <div key={section.section} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace", marginBottom: 10 }}>{section.section}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {section.items.map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{f.label} <span style={{ color: "#334155" }}>({f.unit})</span></div>
                  <input
                    type={f.type}
                    value={logForm[f.key] || ""}
                    onChange={e => setLogForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    style={{
                      width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid #1e293b",
                      borderRadius: 8, padding: "8px 10px", color: "#e2e8f0", fontSize: 13,
                      outline: "none", boxSizing: "border-box",
                      fontFamily: "'DM Mono', monospace",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Notes / How you felt</div>
          <textarea
            value={logForm.notes || ""}
            onChange={e => setLogForm(f => ({ ...f, notes: e.target.value }))}
            rows={3}
            style={{
              width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid #1e293b",
              borderRadius: 8, padding: "10px 12px", color: "#e2e8f0", fontSize: 13,
              outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit"
            }}
            placeholder="Body feel, energy, specific lifts, anything noteworthy..."
          />
        </div>

        <button
          onClick={handleSaveLog}
          disabled={saving}
          style={{
            width: "100%", padding: "14px", background: saved ? "#4ade8022" : "rgba(74,222,128,0.1)",
            border: `1px solid ${saved ? "#4ade80" : "#4ade8033"}`, borderRadius: 10,
            color: saved ? "#4ade80" : "#94a3b8", fontSize: 14, fontWeight: 600, cursor: "pointer",
            transition: "all 0.2s", fontFamily: "monospace", letterSpacing: "0.05em"
          }}
        >
          {saving ? "SAVING..." : saved ? "✓ SAVED" : "SAVE LOG"}
        </button>
      </div>
    );
  }

  // ─── PROGRESS TAB ─────────────────────────────────────────────────────────
  function ProgressTab() {
    const recentLogs = logs.slice(-14);
    const avgSleep = recentLogs.filter(l => l.sleep_hrs).length > 0
      ? (recentLogs.reduce((s, l) => s + (parseFloat(l.sleep_hrs) || 0), 0) / recentLogs.filter(l => l.sleep_hrs).length).toFixed(1)
      : "—";
    const avgHRV = recentLogs.filter(l => l.hrv).length > 0
      ? Math.round(recentLogs.reduce((s, l) => s + (parseFloat(l.hrv) || 0), 0) / recentLogs.filter(l => l.hrv).length)
      : "—";
    const totalZ2 = logs.reduce((s, l) => s + (parseInt(l.zone2_time) || 0), 0);
    const totalMiles = logs.reduce((s, l) => s + (parseFloat(l.run_miles) || 0), 0).toFixed(1);
    const sessionsLogged = logs.filter(l => l.completed).length;

    return (
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", marginBottom: 4 }}>Progress</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>Program-wide metrics · {logs.length} days logged</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          <StatCard label="Total Z2 Time" value={totalZ2} unit="min" color="#4ade80" />
          <StatCard label="Total Miles" value={totalMiles} unit="mi" color="#60a5fa" />
          <StatCard label="Avg Sleep (14d)" value={avgSleep} unit="hrs" color={parseFloat(avgSleep) >= 7 ? "#4ade80" : "#f87171"} />
          <StatCard label="Avg HRV (14d)" value={avgHRV} unit="ms" color="#a78bfa" />
          <StatCard label="Sessions Done" value={sessionsLogged} color="#f97316" />
          <StatCard label="Week Score" value={weekScore !== null ? `${weekScore}%` : "—"} color={scoreColor} sub="this week" />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace", marginBottom: 12 }}>Recent Log History</div>
          {recentLogs.length === 0 ? (
            <div style={{ color: "#334155", fontSize: 13, padding: "20px", textAlign: "center" }}>No logs yet. Start logging from the Log tab.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {recentLogs.slice().reverse().map((l, i) => (
                <div key={i} style={{
                  background: "rgba(255,255,255,0.02)", border: "1px solid #1e293b",
                  borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center"
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#94a3b8", fontFamily: "monospace" }}>{l.date}</div>
                    {l.notes && <div style={{ fontSize: 11, color: "#475569", marginTop: 2, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.notes}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#64748b" }}>
                    {l.zone2_time && <span>Z2 <span style={{ color: "#4ade80" }}>{l.zone2_time}m</span></span>}
                    {l.sleep_hrs && <span>💤 <span style={{ color: parseFloat(l.sleep_hrs) >= 7 ? "#4ade80" : "#f87171" }}>{l.sleep_hrs}h</span></span>}
                    {l.rpe && <span>RPE <span style={{ color: "#f97316" }}>{l.rpe}</span></span>}
                    {l.completed && <span style={{ color: "#4ade80" }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── PLAN TAB ─────────────────────────────────────────────────────────────
  function PlanTab() {
    const [selectedDay, setSelectedDay] = useState(dayName);
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const phase = currentPhase;
    const daySchedule = (day) => {
      const base = DAILY_SCHEDULE[day];
      if (!base) return null;
      if (phase === 2 && PHASE2_OVERRIDES[day]) return { ...base, ...PHASE2_OVERRIDES[day] };
      if (phase === 3 && PHASE3_OVERRIDES[day]) return { ...base, ...PHASE3_OVERRIDES[day] };
      return base;
    };

    return (
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", marginBottom: 4 }}>Training Plan</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>Phase {currentPhase} · Week {currentWeek} · Showing current phase schedule</div>

        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {days.map(d => (
            <button key={d} onClick={() => setSelectedDay(d)} style={{
              padding: "6px 12px", borderRadius: 20, fontSize: 11, cursor: "pointer", fontFamily: "monospace",
              border: selectedDay === d ? "1px solid #4ade80" : "1px solid #1e293b",
              background: selectedDay === d ? "rgba(74,222,128,0.1)" : "transparent",
              color: selectedDay === d ? "#4ade80" : d === dayName ? "#94a3b8" : "#475569",
              fontWeight: d === dayName ? 700 : 400,
            }}>{d.slice(0, 3)}{d === dayName ? " ←" : ""}</button>
          ))}
        </div>

        {daySchedule(selectedDay) ? (
          <>
            {daySchedule(selectedDay).am && <SessionBlock session={daySchedule(selectedDay).am} label="AM" />}
            {daySchedule(selectedDay).pm && <SessionBlock session={daySchedule(selectedDay).pm} label="PM" />}
          </>
        ) : (
          <div style={{ color: "#334155" }}>Rest day.</div>
        )}

        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace", marginBottom: 12 }}>All Phases</div>
          {PHASES.map(p => (
            <div key={p.id} style={{
              padding: "12px 16px", marginBottom: 8,
              background: p.id === currentPhase ? `${p.color}0a` : "rgba(255,255,255,0.02)",
              border: `1px solid ${p.id === currentPhase ? p.color + "44" : "#1e293b"}`,
              borderRadius: 10,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: p.id === currentPhase ? p.color : "#64748b" }}>Phase {p.id}: {p.name}</div>
                <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace" }}>Wk {p.weeks[0]}–{p.weeks[1]}</div>
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── GATES TAB ────────────────────────────────────────────────────────────
  function GatesTab() {
    return (
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", marginBottom: 4 }}>Progress Gates</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>These three metrics determine whether the protocol is working. Enter your actual results below.</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {GATES.map((gate, i) => {
            const val = gateData[gate.field];
            let passed = null;
            if (val) {
              const secs = paceToSeconds(val);
              if (gate.field === "hr_at_11min") passed = parseInt(val) < gate.targetVal;
              else if (secs) passed = gate.direction === "below" ? secs <= gate.targetVal : secs >= gate.targetVal;
            }
            return <GateCard key={i} gate={gate} value={val || null} passed={passed} />;
          })}
        </div>

        <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace", marginBottom: 12 }}>Enter Gate Results</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {GATES.map((gate, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Week {gate.week}: {gate.metric} <span style={{ color: "#334155" }}>(target: {gate.target})</span></div>
              <input
                type="text"
                value={gateForm[gate.field] || ""}
                onChange={e => setGateForm(f => ({ ...f, [gate.field]: e.target.value }))}
                placeholder={gate.field === "hr_at_11min" ? "e.g. 146" : "e.g. 10:45"}
                style={{
                  width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid #1e293b",
                  borderRadius: 8, padding: "8px 10px", color: "#e2e8f0", fontSize: 13,
                  outline: "none", boxSizing: "border-box", fontFamily: "'DM Mono', monospace",
                }}
              />
            </div>
          ))}
        </div>
        <button onClick={handleSaveGates} style={{
          width: "100%", padding: "12px", background: "rgba(74,222,128,0.08)",
          border: "1px solid #4ade8033", borderRadius: 10, color: "#64748b",
          fontSize: 13, cursor: "pointer", fontFamily: "monospace"
        }}>SAVE GATES</button>

        <div style={{ marginTop: 24, padding: "14px 16px", background: "rgba(249,115,22,0.06)", border: "1px solid #f9731622", borderRadius: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#f97316", marginBottom: 6 }}>Race Target</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#f97316", fontFamily: "monospace" }}>70:00</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>TARGET TIME</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#facc15", fontFamily: "monospace" }}>8:35</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>RACE PACE /mi</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#60a5fa", fontFamily: "monospace" }}>Oct 23</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>RACE DATE</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabContent = { Today: <TodayTab />, Log: <LogTab />, Progress: <ProgressTab />, Plan: <PlanTab />, Gates: <GatesTab /> };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: "100vh",
      background: "#080c14",
      color: "#e2e8f0",
      fontFamily: "'Inter', -apple-system, sans-serif",
      maxWidth: 480,
      margin: "0 auto",
      padding: "0 0 80px 0",
    }}>
      {/* Header */}
      <div style={{
        padding: "24px 20px 16px",
        borderBottom: "1px solid #0f1929",
        background: "rgba(8,12,20,0.95)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, color: "#334155", textTransform: "uppercase", letterSpacing: "0.15em", fontFamily: "monospace" }}>HYROX PRO</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>David</div>
            <div style={{ fontSize: 11, color: "#334155", fontFamily: "monospace" }}>6'3" · 215lb · 42 · Oct 23, 2026</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: "#f97316", fontFamily: "monospace", lineHeight: 1 }}>{daysLeft}</div>
            <div style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em" }}>days out</div>
            <div style={{ marginTop: 4, fontSize: 11, padding: "2px 8px", background: phaseInfo ? `${phaseInfo.color}15` : "transparent", color: phaseInfo?.color, borderRadius: 4, fontFamily: "monospace", display: "inline-block" }}>
              {isTaper ? "TAPER" : `PHASE ${currentPhase}`}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "20px" }}>
        {tabContent[tab]}
      </div>

      {/* Nav */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 480, background: "rgba(8,12,20,0.97)",
        borderTop: "1px solid #0f1929", display: "flex", padding: "8px 0 16px",
        backdropFilter: "blur(20px)", zIndex: 10,
      }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, background: "none", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "6px 0",
          }}>
            <div style={{ fontSize: 18 }}>
              {{ Today: "⚡", Log: "📝", Progress: "📈", Plan: "📋", Gates: "🎯" }[t]}
            </div>
            <div style={{ fontSize: 10, color: tab === t ? "#4ade80" : "#334155", fontFamily: "monospace", letterSpacing: "0.05em", fontWeight: tab === t ? 700 : 400 }}>{t.toUpperCase()}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
